import { useRef, type ChangeEvent, type DragEvent, type ReactNode } from 'react'
import { ImageIcon, Upload, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp']
const MAX_SIZE_BYTES = 5 * 1024 * 1024

interface ImagePickerProps {
  previewUrl: string | null
  onSelect: (file: File) => void
  onClear: () => void
  onError: (message: string) => void
}

export function ImagePicker({
  previewUrl,
  onSelect,
  onClear,
  onError,
}: ImagePickerProps): ReactNode {
  const inputRef = useRef<HTMLInputElement>(null)

  function validate(file: File): boolean {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      onError('Formato no soportado. Usa JPG, PNG o WEBP.')
      return false
    }
    if (file.size > MAX_SIZE_BYTES) {
      onError('La imagen supera los 5 MB.')
      return false
    }
    return true
  }

  function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return
    const file = files[0]
    if (!file) return
    if (validate(file)) onSelect(file)
  }

  function onInputChange(e: ChangeEvent<HTMLInputElement>) {
    handleFiles(e.target.files)
    // Reset so selecting the same filename again fires change.
    e.target.value = ''
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault()
    handleFiles(e.dataTransfer.files)
  }

  return (
    <div className="flex flex-col gap-2">
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        className={cn(
          'relative flex min-h-[220px] w-full items-center justify-center overflow-hidden rounded-lg border border-dashed bg-muted/30 transition-colors hover:bg-muted/50',
        )}
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="Vista previa"
            className="h-full max-h-[320px] w-full object-cover"
          />
        ) : (
          <div className="flex flex-col items-center gap-2 p-6 text-center text-muted-foreground">
            <ImageIcon className="h-10 w-10" />
            <p className="text-sm">Arrastra una imagen o seleccionala.</p>
            <p className="text-xs">JPG, PNG o WEBP. Máximo 5 MB.</p>
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_TYPES.join(',')}
          className="hidden"
          onChange={onInputChange}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => inputRef.current?.click()}
        >
          <Upload className="h-4 w-4" />
          {previewUrl ? 'Cambiar imagen' : 'Seleccionar imagen'}
        </Button>
        {previewUrl ? (
          <Button type="button" variant="ghost" size="sm" onClick={onClear}>
            <X className="h-4 w-4" />
            Quitar
          </Button>
        ) : null}
      </div>
    </div>
  )
}
